import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Card } from '@/components/ui/Card';

interface AttributionRow {
  channel: string;
  attributedRevenue: number;
}

type AttributionModel = 'LINEAR' | 'TIME_DECAY' | 'U_SHAPE';

const MODELS: { key: AttributionModel; label: string }[] = [
  { key: 'LINEAR', label: 'attribution.model_linear' },
  { key: 'TIME_DECAY', label: 'attribution.model_time_decay' },
  { key: 'U_SHAPE', label: 'attribution.model_u_shape' },
];

const CHANNEL_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
  X: 'X (Twitter)',
  TIKTOK: 'TikTok',
  WEB_FORM: 'Web form',
  MANUAL: 'Manual',
  CSV_IMPORT: 'CSV import',
  REFERRAL: 'Referral',
  OTHER: 'Other',
};

/**
 * Story 81.3 — Multi-touch attribution page.
 *
 * Model selector pill row + bar chart of revenue-by-source.
 * No chart library — bars are CSS divs consistent with ChannelAnalyticsPage.
 */
export function AttributionPage() {
  const { t } = useTranslation();
  const [model, setModel] = useState<AttributionModel>('LINEAR');
  const [rows, setRows] = useState<AttributionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    apiClient
      .get<AttributionRow[]>(`/analytics/attribution?model=${model}`)
      .then((res) => {
        if (!cancelled) setRows(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(t('attribution.error_load'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [model, t]);

  const maxRevenue = Math.max(1, ...rows.map((r) => r.attributedRevenue));
  const totalRevenue = rows.reduce((s, r) => s + r.attributedRevenue, 0);

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{t('attribution.title')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('attribution.subtitle')}</p>
      </header>

      {/* Model selector pill row */}
      <div className="flex gap-2">
        {MODELS.map((m) => (
          <button
            key={m.key}
            onClick={() => setModel(m.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              model === m.key
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
          >
            {t(m.label)}
          </button>
        ))}
      </div>

      {loading && (
        <p className="text-sm text-muted-foreground">{t('attribution.loading')}</p>
      )}

      {error && !loading && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!loading && !error && rows.length === 0 && (
        <Card>
          <p className="text-sm text-muted-foreground">{t('attribution.empty')}</p>
        </Card>
      )}

      {!loading && !error && rows.length > 0 && (
        <Card>
          <div className="mb-3 flex items-baseline justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('attribution.channel')} / {t('attribution.revenue')}
            </h2>
            <span className="text-xs text-muted-foreground">
              {t('attribution.model_label')}: {t(MODELS.find((m) => m.key === model)?.label ?? '')}
            </span>
          </div>
          <div className="space-y-3">
            {rows.map((row) => (
              <div key={row.channel} className="space-y-1">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">
                    {CHANNEL_LABELS[row.channel] ?? row.channel}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    ${row.attributedRevenue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    {' '}
                    ({totalRevenue > 0
                      ? ((row.attributedRevenue / totalRevenue) * 100).toFixed(1)
                      : '0.0'}%)
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-all duration-300"
                    style={{
                      width: `${(row.attributedRevenue / maxRevenue) * 100}%`,
                      minWidth: row.attributedRevenue > 0 ? '0.25rem' : 0,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
